import React from 'react';
import { Crown, Shield, Swords, Star, Users, Trophy, Sparkles, Skull, Zap } from 'lucide-react';

const ProfileCard = ({ profile, onClick, compact = false }) => {
  const getFlag = () => {
    if (profile.countryFlag) return profile.countryFlag;
    return '🏳️';
  };

  // Remove brackets and content from player name
  const getCleanName = () => {
    if (!profile.playerName) return '';
    return profile.playerName.replace(/\[.*?\]/g, '').trim();
  };

  if (compact) {
    return (
      <div 
        onClick={onClick}
        data-testid={`profile-card-compact-${profile.id}`}
        className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      >
        {/* Outer glow */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 rounded-2xl opacity-50 group-hover:opacity-80 blur-sm transition-opacity"></div>
        
        <div className="relative bg-gradient-to-br from-[#0d2847] via-[#1a3a5c] to-[#0d2847] rounded-xl p-4 border border-cyan-400/50">
          <div className="flex items-center gap-4">
            <div className="relative">
              {profile.profileImage ? (
                <img 
                  src={profile.profileImage} 
                  alt={getCleanName()}
                  className="w-16 h-16 rounded-xl object-cover border-2 border-orange-400 shadow-lg shadow-orange-500/30"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center border-2 border-yellow-300 shadow-lg shadow-orange-500/30">
                  <Shield className="w-8 h-8 text-white drop-shadow-lg" />
                </div>
              )}
              <span className="absolute -bottom-1 -right-1 text-xl drop-shadow-lg">{getFlag()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-3 py-1 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-lg text-white text-sm font-black border border-yellow-300 shadow-lg shadow-orange-500/20">
                  Nvl. {profile.playerLevel}
                </span>
              </div>
              <p className="text-xl font-black text-white truncate" style={{textShadow: '0 0 10px rgba(255,255,255,0.3)'}}>
                {getCleanName()}
              </p>
              <div className="flex items-center gap-3 text-sm text-cyan-300 mt-1">
                <span className="flex items-center gap-1">
                  <Swords className="w-4 h-4 text-yellow-400" />
                  {profile.power}
                </span>
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4 text-cyan-400" />
                  {profile.allianceName}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={onClick}
      data-testid={`profile-card-${profile.id}`}
      className="relative cursor-pointer group"
    >
      {/* Animated outer glow border */}
      <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500 rounded-3xl opacity-75 blur-sm group-hover:opacity-100 transition-opacity animate-pulse"></div>
      
      {/* Main card */}
      <div className="relative bg-gradient-to-b from-[#0d2847] via-[#1a3a5c] to-[#0d2847] rounded-2xl overflow-hidden border-2 border-yellow-500/80">
        
        {/* Top shine effect */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>
        
        {/* Header section with avatar and name */}
        <div className="relative p-6 pb-4">
          {/* Decorative corner elements */}
          <div className="absolute top-2 left-2 w-8 h-8 border-l-2 border-t-2 border-yellow-500/50 rounded-tl-lg"></div>
          <div className="absolute top-2 right-2 w-8 h-8 border-r-2 border-t-2 border-yellow-500/50 rounded-tr-lg"></div>
          
          <div className="flex items-start gap-5">
            {/* Avatar with glow effect - Larger size for better visibility */}
            <div className="relative flex-shrink-0">
              <div className="absolute -inset-1 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl blur-md opacity-60"></div>
              {profile.profileImage ? (
                <img 
                  src={profile.profileImage} 
                  alt={getCleanName()}
                  className="relative w-32 h-40 sm:w-36 sm:h-44 rounded-xl object-cover object-top border-3 border-yellow-400 shadow-2xl"
                />
              ) : (
                <div className="relative w-32 h-40 sm:w-36 sm:h-44 rounded-xl bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 flex items-center justify-center border-3 border-yellow-300 shadow-2xl">
                  <Shield className="w-14 h-14 text-white drop-shadow-lg" />
                </div>
              )}
              {/* Flag badge */}
              <div className="absolute -bottom-2 -right-2 w-10 h-10 sm:w-12 sm:h-12 bg-[#0d2847] rounded-full flex items-center justify-center border-2 border-cyan-400 shadow-lg">
                <span className="text-xl sm:text-2xl">{getFlag()}</span>
              </div>
            </div>

            {/* Player info */}
            <div className="flex-1 pt-2">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-4 py-1.5 bg-gradient-to-r from-orange-500 via-yellow-500 to-orange-500 rounded-xl text-white font-black text-lg border-2 border-yellow-300 shadow-lg shadow-orange-500/30"
                      style={{textShadow: '1px 1px 2px rgba(0,0,0,0.5)'}}>
                  Nvl. {profile.playerLevel}
                </span>
                <span className="text-2xl text-cyan-300">{profile.gender === 'male' ? '♂' : '♀'}</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white mb-1"
                  style={{textShadow: '0 0 20px rgba(255,255,255,0.4), 2px 2px 4px rgba(0,0,0,0.5)'}}>
                {getCleanName()}
              </h3>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="text-cyan-300/80 text-sm font-semibold">Guerrero de Last War</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid - Beautiful cards */}
        <div className="px-6 pb-4">
          <div className="grid grid-cols-2 gap-3">
            {/* Power */}
            <div className="relative group/stat">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl opacity-0 group-hover/stat:opacity-50 blur transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-[#0a1f3a] to-[#0d2847] rounded-xl p-4 border border-yellow-500/30 hover:border-yellow-500/60 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/30 border border-yellow-300/50">
                    <Swords className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-cyan-300 font-bold text-sm">Poder</span>
                </div>
                <p className="text-2xl font-black text-yellow-400 pl-1"
                   style={{textShadow: '0 0 15px rgba(250, 204, 21, 0.6)'}}>
                  {profile.power}
                </p>
              </div>
            </div>

            {/* Muertes */}
            <div className="relative group/stat">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 to-red-500 rounded-xl opacity-0 group-hover/stat:opacity-50 blur transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-[#0a1f3a] to-[#0d2847] rounded-xl p-4 border border-pink-500/30 hover:border-pink-500/60 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center shadow-lg shadow-pink-500/30 border border-pink-300/50">
                    <Skull className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-cyan-300 font-bold text-sm">Muertes</span>
                </div>
                <p className="text-2xl font-black text-pink-400 pl-1"
                   style={{textShadow: '0 0 15px rgba(236, 72, 153, 0.6)'}}>
                  {profile.troopPower}
                </p>
              </div>
            </div>

            {/* LV Profesión */}
            <div className="relative group/stat">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl opacity-0 group-hover/stat:opacity-50 blur transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-[#0a1f3a] to-[#0d2847] rounded-xl p-4 border border-green-500/30 hover:border-green-500/60 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30 border border-green-300/50">
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-cyan-300 font-bold text-sm">LV Profesión</span>
                </div>
                <p className="text-2xl font-black text-green-400 pl-1"
                   style={{textShadow: '0 0 15px rgba(74, 222, 128, 0.6)'}}>
                  Nvl. {profile.commanderLevel}
                </p>
              </div>
            </div>

            {/* VIP */}
            <div className="relative group/stat">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-violet-500 rounded-xl opacity-0 group-hover/stat:opacity-50 blur transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-[#0a1f3a] to-[#0d2847] rounded-xl p-4 border border-purple-500/30 hover:border-purple-500/60 transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center shadow-lg shadow-purple-500/30 border border-purple-300/50">
                    <Star className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-cyan-300 font-bold text-sm">VIP</span>
                </div>
                <p className="text-2xl font-black text-purple-400 pl-1"
                   style={{textShadow: '0 0 15px rgba(192, 132, 252, 0.6)'}}>
                  Nvl. {profile.vipLevel}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Alliance Section - Premium look */}
        <div className="px-6 pb-6">
          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 rounded-xl opacity-60 blur-sm"></div>
            <div className="relative bg-gradient-to-r from-[#0a2540] via-[#0d3050] to-[#0a2540] rounded-xl p-5 border border-cyan-400/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-cyan-500 rounded-xl blur-md opacity-40"></div>
                    <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center border-2 border-cyan-300 shadow-lg">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div>
                    <p className="text-cyan-400 font-bold text-sm mb-0.5">{profile.allianceRank}</p>
                    <p className="text-white font-black text-xl" style={{textShadow: '0 0 10px rgba(255,255,255,0.3)'}}>
                      {profile.allianceName}
                    </p>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="flex items-center gap-2 text-yellow-400 mb-1">
                    <Trophy className="w-5 h-5" />
                    <span className="font-bold text-sm">Servidor</span>
                  </div>
                  <p className="text-3xl font-black text-yellow-400"
                     style={{textShadow: '0 0 20px rgba(250, 204, 21, 0.6)'}}>
                    #{profile.castleRank}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom decorative elements */}
        <div className="absolute bottom-2 left-2 w-8 h-8 border-l-2 border-b-2 border-yellow-500/50 rounded-bl-lg"></div>
        <div className="absolute bottom-2 right-2 w-8 h-8 border-r-2 border-b-2 border-yellow-500/50 rounded-br-lg"></div>
        
        {/* Sparkle effects */}
        <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <Sparkles className="w-8 h-8 text-yellow-400 animate-pulse" />
        </div>
        <div className="absolute bottom-20 left-6 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <Zap className="w-6 h-6 text-cyan-400 animate-pulse" />
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
